import { useEffect, useRef } from 'react';

/**
 * Attaches an IntersectionObserver to the returned ref's element.
 * When the element enters the viewport, `.reveal-visible` is added to it
 * and all descendant elements that carry a `reveal`, `reveal-left`,
 * `reveal-right`, or `reveal-scale` class.
 *
 * Usage:
 *   const ref = useScrollReveal();
 *   <section ref={ref}>
 *     <h2 className="reveal">...</h2>
 *     <p  className="reveal reveal-delay-200">...</p>
 *   </section>
 */
export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options: IntersectionObserverInit = {}
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const { threshold = 0.12, rootMargin = '0px 0px -60px 0px', ...rest } = options;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          // Mark the section itself and all reveal children
          const targets = entry.target.querySelectorAll<HTMLElement>(
            '.reveal, .reveal-left, .reveal-right, .reveal-scale'
          );
          targets.forEach((t) => t.classList.add('reveal-visible'));

          // Once animated, no need to keep watching
          observer.unobserve(entry.target);
        });
      },
      { threshold, rootMargin, ...rest }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return ref;
}
